import { List } from "immutable";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Autocomplete } from "@/Common/Components/Inputs/Autocomplete";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { Popup } from "@/Common/Components/Modals/Popup";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { CallCenterCallReason } from "@/Common/Models/CallCenter/CallCenterCallReason";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { IPatientCommunicationsModel } from "@/Patient/Models/UI/History/IPatientCommunicationsModel";
import { PatientCommunicationDeletionPopup } from "./PatientCommunicationDeletionPopup";

interface IPatientHistoryCommunicationsProps {
    Model: IPatientCommunicationsModel;
    PatientModel: Patient;
}

class CommunicationsPopupState {
    public isSaving: boolean;
    public showDeletePopup: boolean;
    public selectedClinician?: Person;
    public selectedCallReasons: List<CallCenterCallReason>;
}

class CommunicationsPopup extends React.Component<IPatientHistoryCommunicationsProps, CommunicationsPopupState> {
    private readonly activeClinicians: Person[];

    constructor(props: IPatientHistoryCommunicationsProps) {
        super(props);
        this.state = {
            isSaving: false,
            showDeletePopup: false,
            selectedClinician: null,
            selectedCallReasons: List<CallCenterCallReason>()
        };
        this.activeClinicians = cacheStore.GetClinicians(false);
    }

    public render(): JSX.Element {
        if (cacheStore.ClinicSettings.IsCallCenterEnabled()) {
            return this.renderPatientCorrespondenceNewAndEditForm();
        }
        else {
            return this.renderPatientCorrespondenceNewForm();
        }
    }

    private renderPatientCorrespondenceNewForm(): JSX.Element {
        let communicationsModel: IPatientCommunicationsModel;
        if (this.props.Model) {
            communicationsModel = this.props.Model;
        }

        return (
            <Popup title={cacheStore.GetString("NewEntryLabel")} cssClass="double-booking" closeTarget={LocalPatientOperations.History.Communications_CancelAddNewCommunications}>
                <div className="p-body">
                    <select onChange={this.OnStatusChange.bind(this)} className="form-control myle-control" value={communicationsModel.get("SelectedStatusId")}>
                        {communicationsModel.get("Statuses").toArray().map(s => (
                            <option key={s.LookupID} value={s.LookupID}> {cacheStore.GetLocalizedValueByLookupId(s.LookupID)}</option>
                        ))}
                    </select>
                    <textarea className="form-control" onChange={this.OnNotesChange.bind(this)} id="history_create_communications" disabled={false} rows={4} style={{ maxHeight: "300px", marginTop: "5px" }} />
                </div>
                <div className="p-footer">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 flex-display flex-end flex-gap-xs">
                            <ButtonControl
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.History.Communications_CancelAddNewCommunications)}
                                label={cacheStore.GetString("CancelLabel")}
                                className="btn btn-default"
                            />
                            <ButtonControl
                                label={cacheStore.GetString("OKLabel")}
                                className="btn btn-primary"
                                onClick={() => {
                                    sessionStore.dispatch(LocalPatientOperations.History.Communications_OkAddNewCommunications);
                                    this.mutateState(s => s.isSaving = true);
                                }}
                                disabled={this.state.isSaving}
                            />
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }

    private renderPatientCorrespondenceNewAndEditForm(): JSX.Element {
        const { Model } = this.props;
        const selectedStatusId: string = Model.get("SelectedStatusId");
        const incomingOutgoingCode: string = selectedStatusId ? cacheStore.GetCodeByLookupId(selectedStatusId) : null;
        const hideCallFields: boolean = !(incomingOutgoingCode == "IncomingCall" || incomingOutgoingCode == "OutgoingCall");
        const showInEditMode: boolean = Model.get("ShowEditMode");
        const popupClass: string = this.state.showDeletePopup ? "correspondence-overlay" : "correspondence";

        return (
            <div>
                {this.state.showDeletePopup && (

                    <PatientCommunicationDeletionPopup
                        onCancel={() => this.handleDeletePopupCancelClick()}
                        onSubmit={() => this.handleDeletePopupSubmitClick()}
                    />
                )}
                <Popup
                    title={this.getPopupTitle(showInEditMode)}
                    cssClass={popupClass}
                    centeredHorizontally
                    draggable
                    closeTarget={LocalPatientOperations.History.Communications_CancelAddNewCommunications}
                >
                    <div className="p-body">
                        {this.renderStatusSection(showInEditMode, selectedStatusId)}
                        {this.renderCallFields(hideCallFields, showInEditMode)}
                        {this.renderNoteField(hideCallFields)}
                    </div>
                    <div className="p-footer">
                        {this.renderSubmitButton(hideCallFields, showInEditMode)}
                    </div>
                </Popup>
            </div>

        );
    }

    private renderStatusSection(showInEditMode: boolean, selectedStatusId: string): JSX.Element {
        const { Model } = this.props;
        const statuses = Model.get("Statuses") || Immutable.List();
        if (showInEditMode) {
            return (
                <p>
                    <strong>{cacheStore.GetString("CallCenter_Correspondence_CorrespondenceType")}: </strong>
                    {cacheStore.GetLocalizedValueByLookupId(selectedStatusId)}
                </p>
            );
        }
        else {
            return (
                <select
                    onChange={this.OnStatusChange}
                    className="form-control myle-control"
                    value={selectedStatusId || ""}
                >
                    {statuses.map(s => (
                        <option key={s.LookupID} value={s.LookupID}>
                            {cacheStore.GetLocalizedValueByLookupId(s.LookupID)}
                        </option>
                    ))}
                </select>
            );
        }
    }

    private renderCallFields(hideCallFields: boolean, showInEditMode: boolean): JSX.Element | null {
        if (hideCallFields) {
            return null;
        }

        return (
            <div>
                {this.renderDateTimeSelectors(showInEditMode)}
                {this.renderPersonSearchAutocomplete(showInEditMode)}
                {this.renderCallReasons()}
            </div>
        );
    }

    private renderDateTimeSelectors(showInEditMode: boolean): JSX.Element {
        const { Model } = this.props;
        if (showInEditMode) {
            return (
                <div className="divLabel">
                    <label className="no-margin">
                        <strong>{cacheStore.GetString("CallCenter_Correspondence_DateOfCall")}: </strong>
                        {this.getCallDateAndTimeValues(Model.get("CallStartDateTime"), Model.get("CallEndTime"))}
                    </label>
                </div>
            );
        }

        return (
            <div>
                <div className="divLabel">
                    <label className="no-margin">
                        <strong>{cacheStore.GetString("CallCenter_Correspondence_DateOfCall")}</strong>
                    </label>
                </div>

                <div style={{ display: "flex" }}>
                    <DateSelector
                        id="new_call_startdate"
                        onSelectOp={LocalPatientOperations.History.Communications_CallDateChange}
                        disabled={false}
                        width={187}
                        height={36}
                        date={Model.get("CallStartDateTime")}
                        dateFormat="yyyy-MM-dd"
                    />
                    <TimeSelector
                        id="new_call_starttime"
                        date={Model.get("CallStartDateTime")}
                        isRequired
                        dateFormat="HH:mm"
                        width={91}
                        height={36}
                        interval={15}
                        cssClass="callStartTime"
                        onSelectOp={LocalPatientOperations.History.Communications_CallTimeStartChange}
                        disabled={false}
                    />
                    <label className="callTimeDivider">
                        {cacheStore.GetString("ToDLabel")}
                    </label>
                    <TimeSelector
                        id="new_call_endtime"
                        date={Model.get("CallEndTime")}
                        isRequired
                        interval={15}
                        dateFormat="HH:mm"
                        width={91}
                        height={36}
                        onSelectOp={LocalPatientOperations.History.Communications_CallTimeEndChange}
                        disabled={false}
                    />
                </div>
            </div>
        );
    }

    private renderPersonSearchAutocomplete(showInEditMode: boolean): JSX.Element {
        const { Model } = this.props;
        const selectedClinician: Person | null = Model?.get("SelectedClinician");

        if (showInEditMode) {
            return (
                <div className="divLabel">
                    <label className="no-margin">
                        <strong>{cacheStore.GetString("CallCenter_Correspondence_UserWhoHandledCall")}:</strong> {Model.get("FromEmployee")}
                    </label>
                </div>
            );
        }

        return (
            <div>
                <div className="divLabel">
                    <label className="no-margin">
                        <strong>{cacheStore.GetString("CallCenter_Correspondence_UserWhoHandledCall")}</strong>
                    </label>
                </div>
                <div style={{ width: "235px" }}>
                    <PersonSearchAutocompleteCustom
                        disabled={false}
                        componentId="add_call_clinicians"
                        focusOnLoad={false}
                        closeDropDownAfterSelection={true}
                        searchStrategy={new ClinicianSearchStrategy(this.activeClinicians)}
                        allowMultipleItemSelection={false}
                        selectedItemList={Immutable.List(selectedClinician ? [selectedClinician] : [])}
                        selectionOp={LocalPatientOperations.History.Communications_ClinicianAdded}
                        clearOp={LocalPatientOperations.History.Communications_ClinicianCleared}
                        dropDownListMaxHeight={200}
                    />
                </div>
            </div>
        );
    }

    private renderCallReasons(): JSX.Element {
        const { Model } = this.props;
        const selectedCallReasons = Model?.get("CallReasons") || Immutable.List();

        return (
            <div>
                <div className="divLabel">
                    <label className="no-margin">
                        <strong>{cacheStore.GetString("CallCenter_Correspondence_CallReason")}</strong>
                    </label>
                </div>
                <div>
                    <Autocomplete
                        selectionOp={LocalPatientOperations.History.Communications_CallReasonChange}
                        list={Immutable.List(this.enumToArray(CallCenterCallReason))}
                        selectedValue={Immutable.List(selectedCallReasons)}
                        componentId="tags_autocomplete"
                        autoClose={true}
                    />
                </div>
            </div>
        );
    }

    private renderNoteField(hideCallFields: boolean): JSX.Element {
        const { Model } = this.props;
        return (
            <div>
                <div className="divLabel">
                    <label className="no-margin">
                        <strong>{cacheStore.GetString("NotesLabel")}</strong>
                    </label>
                </div>
                <div>
                    <textarea
                        className="form-control"
                        onChange={this.OnNotesChange}
                        id="history_create_communications"
                        disabled={false}
                        value={Model.get("Note")}
                        rows={hideCallFields ? 4 : 1}
                        style={{ maxHeight: "300px", marginTop: "5px", maxWidth: "100%" }}
                    />
                </div>
            </div>
        );
    }

    private renderSubmitButton(hideCallFields: boolean, showInEditMode: boolean): JSX.Element {
        const { isSaving } = this.state;

        const isSubmitDisabled = this.submitHandleLogic(hideCallFields, showInEditMode);
        const onClickHandler = this.determineOnClickHandler(hideCallFields, showInEditMode);

        return (
            <div className="row">
                <div className="col-md-6 col-sm-6  text-left">
                    {showInEditMode && (
                        <ButtonControl
                            label={cacheStore.GetString("DeleteLabel")}
                            className="btn btn-danger text-left"
                            onClick={() => this.handleShowDeletePopupClick()}
                        />
                    )}
                </div>
                <div className="col-md-6 col-sm-6  text-right">
                    <ButtonControl
                        onClick={() => sessionStore.dispatch(LocalPatientOperations.History.Communications_CancelAddNewCommunications)}
                        label={cacheStore.GetString("CancelLabel")}
                        className="btn btn-default push-right-5"
                    />
                    <ButtonControl
                        label={cacheStore.GetString("OKLabel")}
                        className="btn btn-primary"
                        onClick={onClickHandler}
                        IsDisabled={isSaving || isSubmitDisabled}
                    />
                </div>
            </div>
        );
    }

    private handleDeletePopupCancelClick(): void {
        this.setState({ showDeletePopup: false });
    }

    private handleDeletePopupSubmitClick(): void {
        this.setState({ isSaving: true });
        sessionStore.dispatch(LocalPatientOperations.History.Communications_DeleteCommunications);
    }

    private handleShowDeletePopupClick(): void {
        this.setState({ showDeletePopup: true });
    }

    private submitHandleLogic(hideCallFields: boolean, showInEditMode: boolean): boolean {
        const { Model } = this.props;

        if (hideCallFields || showInEditMode)
            return false;

        const isClinicianSelected = Boolean(Model.get("SelectedClinician"));
        const isCallEndTimeValid = Model?.get("CallEndTime") <= Model?.get("CallStartDateTime");

        return !isClinicianSelected || isCallEndTimeValid;
    }

    private determineOnClickHandler(hideCallFields: boolean, showInEditMode: boolean): () => void {
        if (showInEditMode) {
            return this.handleEditCallCommunicationsSubmit.bind(this);
        }
        else if (hideCallFields) {
            return this.handleNewCommunicationSubmit.bind(this);
        }
        return this.handleNewCallCommunicationSubmit.bind(this);
    }

    private getPopupTitle(showInEditMode: boolean): string {
        return showInEditMode ? cacheStore.GetString("EDITLabel") : cacheStore.GetString("NewEntryLabel");
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    private mutateState(mutation: (s: CommunicationsPopupState) => void, then: () => void = null): void {
        const s = new CommunicationsPopupState();
        mutation(s);
        this.setState(s, then);
    }

    private handleNewCommunicationSubmit(): void {
        this.setState({ isSaving: true });
        sessionStore.dispatch(LocalPatientOperations.History.Communications_OkAddNewCommunications);
    }

    private handleNewCallCommunicationSubmit(): void {
        this.setState({ isSaving: true });
        sessionStore.dispatch(LocalPatientOperations.History.Communications_OkAddNewCallCommunications);
    }

    private handleEditCallCommunicationsSubmit(): void {
        this.setState({ isSaving: true });
        sessionStore.dispatch(LocalPatientOperations.History.Communications_OkEditCallCommunications);
    }

    public OnStatusChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const operation = new LocalEvent(LocalPatientOperations.History.Communications_StatusChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    public OnNotesChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        const operation = new LocalEvent(LocalPatientOperations.History.Communications_NoteTextChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }
    private getCallDateAndTimeValues(callStartDateTime: Date, callEndTime: Date | null): string {
        let formattedEndTime = "";
        if (callEndTime) {
            const endDateTime = LuxonDateTimeParser.parse(callEndTime).toLocal();
            formattedEndTime = endDateTime.toFormat("-HH:mm");
        }

        const startDateTime = LuxonDateTimeParser.parse(callStartDateTime).toLocal();

        const formattedDate = startDateTime.toFormat("yyyy/MM/dd");
        const formattedStartTime = startDateTime.toFormat("HH:mm");

        const fromlabel = cacheStore.GetString("FromDLabel");
        return `${formattedDate} ${fromlabel} ${formattedStartTime}${formattedEndTime}`;
    }

    private enumToArray(enumObj: any): KeyValuePair<number, string>[] {
        return Object.entries(enumObj)
            .filter(([, value]) => typeof value === "number")
            .map(([key, value]) => ({
                Key: +value,
                Value: cacheStore.FindLookupByLookupCode(key, "CallCenter_CallReason")?.GetValue() ?? key,
                Active: true
            }));
    }
}

export { CommunicationsPopup, IPatientHistoryCommunicationsProps };
